import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Buildings, CheckCircle, GlobeHemisphereWest, Handshake,
  MagnifyingGlass, SealCheck, UsersThree,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import InvestorOwnershipForm from "@/components/InvestorOwnershipForm";

const fieldClass =
  "h-11 border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const titleCase = (value) =>
  String(value || "—")
    .replaceAll("_", " ")
    .replace(/\\b\\w/g, (letter) => letter.toUpperCase());

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    gold: "border-[#C5A059]/40 bg-[#C5A059]/10 text-[#8A692D]",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return (
    <span className={`inline-flex items-center border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
}

function StatCard({ icon: Icon, value, label, gold = false }) {
  return (
    <div className="flex min-h-[112px] items-center gap-4 border border-slate-200 bg-white px-6 py-5">
      <div className={`flex h-11 w-11 items-center justify-center ${gold ? "text-[#B48736]" : "text-[#0B2545]"}`}>
        <Icon size={27} />
      </div>
      <div>
        <p className="font-serif text-2xl text-[#0B2545]">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function ProfileCard({ profile, t }) {
  const primaryContact = (profile.contacts || []).find(
    (contact) => contact.is_primary
  ) || (profile.contacts || [])[0];

  const confidenceTone =
    profile.confidence_level === "high"
      ? "green"
      : profile.confidence_level === "medium"
        ? "amber"
        : "slate";

  return (
    <article className="flex h-full flex-col border border-slate-200 bg-white">
      <div className="h-1 bg-gradient-to-r from-[#0B2545] via-[#C5A059] to-[#0B2545]" />
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-[0.2em] text-[#A77E35]">
              {titleCase((profile.profile_types || [])[0])}
            </p>
            <h2 className="font-serif text-xl leading-tight text-[#0B2545]">
              {profile.name}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {[profile.headquarters_city, profile.headquarters_country]
                .filter(Boolean)
                .join(", ") || "—"}
            </p>
          </div>
          <Badge tone="gold">{profile.priority || "C"}</Badge>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 border-y border-slate-100 py-4">
          <Badge tone={confidenceTone}>
            {titleCase(profile.confidence_level)}
          </Badge>
          <Badge tone={profile.evidence_status === "officially_verified" ? "green" : "amber"}>
            {titleCase(profile.evidence_status)}
          </Badge>
          {(profile.investment_geographies || []).some((item) => String(item).toLowerCase().includes("portugal")) && (
            <Badge tone="blue">
              {t("investorOwnership.portugalPresence", "Portugal presence")}
            </Badge>
          )}
        </div>

        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">
            {t("investorOwnership.sectors", "Investment sectors")}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {(profile.investment_sectors || []).slice(0, 4).map((item) => (
              <span key={item} className="bg-slate-50 px-2 py-1 text-xs text-slate-600">
                {titleCase(item)}
              </span>
            ))}
          </div>
        </div>

        <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600">
          {profile.why_relevant || profile.description ||
            t("investorOwnership.noDescription", "No profile description recorded.")}
        </p>

        <div className="mt-auto pt-5">
          <div className="border-t border-slate-100 pt-4 text-xs text-slate-500">
            {primaryContact?.email
              ? primaryContact.email
              : t("investorOwnership.contactMissing", "Professional contact requires verification")}
          </div>
          {profile.official_url && (
            <a
              href={profile.official_url}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex text-xs font-semibold uppercase tracking-wider text-[#0B2545] hover:text-[#A77E35]"
            >
              {t("investorOwnership.officialWebsite", "Official website")} →
            </a>
          )}
        </div>
      </div>
    </article>
  );
}


export default function InvestorOwnership() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [profileType, setProfileType] = useState("");
  const [sector, setSector] = useState("");
  const [priority, setPriority] = useState("");
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();

  const params = useMemo(() => {
    const result = { limit: 500 };
    if (search.trim()) result.q = search.trim();
    if (country) result.country = country;
    if (profileType) result.profile_type = profileType;
    if (sector) result.sector = sector;
    if (priority) result.priority = priority;
    return result;
  }, [search, country, profileType, sector, priority]);

  const { data: meta } = useQuery({
    queryKey: ["investor-ownership-meta"],
    queryFn: async () =>
      (await api.get("/investor-ownership/meta")).data,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["investor-ownership", params],
    queryFn: async () =>
      (await api.get("/investor-ownership", { params })).data,
  });

  const profiles = useMemo(() => data?.results || [], [data]);

  const createMutation = useMutation({
    mutationFn: async (payload) =>
      (await api.post("/investor-ownership", payload)).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["investor-ownership"],
      });
      setShowForm(false);
      toast.success(
        t(
          "investorOwnership.form.created",
          "Construction profile created successfully."
        )
      );
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.detail ||
          t(
            "investorOwnership.form.createError",
            "The intelligence profile could not be created."
          )
      );
    },
  });

  const countries = useMemo(() => {
    const values = new Set();
    profiles.forEach((profile) => {
      if (profile.headquarters_country) {
        values.add(profile.headquarters_country);
      }
      (profile.countries_of_operation || []).forEach((item) => {
        if (item) values.add(item);
      });
    });
    return Array.from(values).sort();
  }, [profiles]);

  const statistics = useMemo(() => ({
    total: data?.total || 0,
    priority: profiles.filter(
      (profile) => profile.priority === "A"
    ).length,
    verified: profiles.filter(
      (profile) => profile.evidence_status === "officially_verified"
    ).length,
    portugal: profiles.filter(
      (profile) => (profile.investment_geographies || []).some((item) => String(item).toLowerCase().includes("portugal"))
    ).length,
  }), [profiles, data?.total]);

  return (
    <div className="space-y-8 pb-12">
      <header className="flex flex-col justify-between gap-6 xl:flex-row xl:items-end">
        <div>
          <p className="mb-3 text-xs uppercase tracking-[0.28em] text-[#A77E35]">
            {t(
              "investorOwnership.eyebrow",
              "Panatlantik delivery intelligence"
            )}
          </p>
          <h1 className="font-serif text-4xl leading-tight text-[#0B2545] md:text-5xl">
            {t(
              "investorOwnership.title",
              "Investor & Ownership Intelligence"
            )}
          </h1>
          <p className="mt-3 max-w-3xl text-base leading-7 text-slate-500">
            {t(
              "investorOwnership.subtitle",
              "Verified contractors, developers, engineering groups and strategic partners matched to Panatlantik projects."
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/investor-ownership/discover")}
            className="h-11 rounded-none border-[#C5A059] px-5"
          >
            <GlobeHemisphereWest size={18} className="mr-2" />
            {t(
              "investorOwnership.discover",
              "Discover profiles"
            )}
          </Button>
          <Button
            type="button"
            onClick={() => setShowForm(true)}
            className="h-11 rounded-none bg-[#0B2545] px-5 hover:bg-[#12345e]"
          >
            <Buildings size={18} className="mr-2" />
            {t(
              "investorOwnership.addProfile",
              "Add profile"
            )}
          </Button>
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Buildings}
          value={statistics.total}
          label={t("investorOwnership.total", "Total profiles")}
        />
        <StatCard
          icon={Handshake}
          value={statistics.priority}
          label={t("investorOwnership.priorityA", "Priority A partners")}
          gold
        />
        <StatCard
          icon={SealCheck}
          value={statistics.verified}
          label={t("investorOwnership.verified", "Verified profiles")}
        />
        <StatCard
          icon={UsersThree}
          value={statistics.portugal}
          label={t("investorOwnership.portugal", "Operating in Portugal")}
          gold
        />
      </section>

      <section className="border border-slate-200 bg-white p-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(280px,1fr)_repeat(4,minmax(150px,0.35fr))]">
          <label className="relative">
            <MagnifyingGlass
              size={19}
              className="absolute left-4 top-3 text-slate-400"
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t(
                "investorOwnership.search",
                "Search investor, organisation, geography or sector…"
              )}
              className={`${fieldClass} w-full pl-11`}
            />
          </label>

          <select
            value={country}
            onChange={(event) => setCountry(event.target.value)}
            className={fieldClass}
          >
            <option value="">
              {t("investorOwnership.allCountries", "All countries")}
            </option>
            {countries.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>

          <select
            value={profileType}
            onChange={(event) => setProfileType(event.target.value)}
            className={fieldClass}
          >
            <option value="">
              {t("investorOwnership.allProfileTypes", "All profile types")}
            </option>
            {(meta?.profile_types || []).map((item) => (
              <option key={item} value={item}>{titleCase(item)}</option>
            ))}
          </select>

          <select
            value={sector}
            onChange={(event) => setSector(event.target.value)}
            className={fieldClass}
          >
            <option value="">
              {t("investorOwnership.allSectors", "All investment sectors")}
            </option>
            {(meta?.investment_sectors || []).map((item) => (
              <option key={item} value={item}>{titleCase(item)}</option>
            ))}
          </select>

          <select
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
            className={fieldClass}
          >
            <option value="">
              {t("investorOwnership.allPriorities", "All priorities")}
            </option>
            {(meta?.priorities || []).map((item) => (
              <option key={item} value={item}>
                {t("investorOwnership.priority", "Priority")} {item}
              </option>
            ))}
          </select>
        </div>
      </section>

      <div className="border-l-2 border-[#C5A059] bg-slate-100 px-5 py-4 text-sm text-[#0B2545]">
        {t(
          "investorOwnership.notice",
          "Internal partner intelligence. Verify profile and contact information before external communication."
        )}
      </div>

      {isLoading ? (
        <div className="py-20 text-center text-slate-500">
          {t("investorOwnership.loading", "Loading investor and ownership profiles…")}
        </div>
      ) : isError ? (
        <div className="border border-red-200 bg-red-50 p-6 text-red-700">
          {t(
            "investorOwnership.loadError",
            "Investor & Ownership Intelligence could not be loaded."
          )}
        </div>
      ) : profiles.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white py-20 text-center">
          <CheckCircle size={36} className="mx-auto text-[#C5A059]" />
          <h2 className="mt-4 font-serif text-2xl text-[#0B2545]">
            {t(
              "investorOwnership.noResults",
              "No intelligence profiles found"
            )}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {t(
              "investorOwnership.noResultsHelp",
              "Import the researched profiles or adjust the search filters."
            )}
          </p>
        </div>
      ) : (
        <section className="grid items-stretch gap-5 lg:grid-cols-2 xl:grid-cols-3">
          {profiles.map((profile) => (
            <ProfileCard
              key={profile.id}
              profile={profile}
              t={t}
            />
          ))}
        </section>
      )}
      {showForm && (
        <InvestorOwnershipForm
          meta={meta}
          saving={createMutation.isPending}
          onCancel={() => setShowForm(false)}
          onSave={(payload) => createMutation.mutate(payload)}
          t={t}
        />
      )}
    </div>
  );
}
